import { motion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { NavLink } from "react-router-dom";
import { LiveCountBadge } from "@/components/DiscordWidget";

const DISCORD_URL = "https://discord.gg/FsFnn9NuF3";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: [0.21, 0.65, 0.35, 1] },
  }),
};

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative flex items-center min-h-screen pt-24 pb-16"
    >
      <div className="absolute inset-0 grid-pattern pointer-events-none" />
      <div
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[500px] pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(147,51,234,0.22) 0%, rgba(9,10,15,0) 70%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial="hidden"
              animate="visible"
              custom={0}
              variants={fadeUp}
              className="inline-flex items-center gap-2 rounded-full border border-purple-500/25 bg-purple-500/5 px-4 py-1.5 mb-7"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-purple-400 shadow-[0_0_8px_2px_rgba(168,85,247,0.7)]" />
              <span className="text-xs font-medium tracking-[0.2em] uppercase text-purple-300">
                Minecraft Development Community
              </span>
            </motion.div>

            <motion.h1
              initial="hidden"
              animate="visible"
              custom={1}
              variants={fadeUp}
              data-testid="hero-heading"
              className="text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight text-white leading-[1.08]"
            >
              Build Beyond{" "}
              <span className="bg-gradient-to-r from-purple-400 via-fuchsia-400 to-purple-500 bg-clip-text text-transparent">
                Limits.
              </span>
            </motion.h1>

            <motion.p
              initial="hidden"
              animate="visible"
              custom={2}
              variants={fadeUp}
              data-testid="hero-subtitle"
              className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
            >
              Professional Minecraft development, custom plugins, and
              innovative solutions for servers and communities that want to
              stand out.
            </motion.p>

            <motion.div
              initial="hidden"
              animate="visible"
              custom={3}
              variants={fadeUp}
              className="mt-9 flex flex-col sm:flex-row gap-4"
            >
              <a
                href={DISCORD_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-join-discord-button"
                className="btn-glow inline-flex items-center justify-center gap-2 rounded-xl bg-purple-600 hover:bg-purple-500 px-7 py-3.5 text-sm sm:text-base font-semibold text-white shadow-[0_0_28px_rgba(168,85,247,0.45)] hover:shadow-[0_0_40px_rgba(168,85,247,0.7)]"
              >
                Join Our Discord
                <ArrowRight className="w-4 h-4" />
              </a>
              <NavLink
                to="/services"
                data-testid="hero-explore-services-button"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900/70 hover:bg-slate-800 border border-purple-500/30 hover:border-purple-500/60 px-7 py-3.5 text-sm sm:text-base font-semibold text-slate-200 transition-all duration-300 hover:-translate-y-0.5"
              >
                Explore Our Services
                <ChevronDown className="w-4 h-4" />
              </NavLink>
            </motion.div>

            <motion.div
              initial="hidden"
              animate="visible"
              custom={4}
              variants={fadeUp}
              className="mt-8"
            >
              <LiveCountBadge />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.35, ease: [0.21, 0.65, 0.35, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="absolute -inset-6 bg-purple-600/15 blur-3xl rounded-full pointer-events-none" />
            <div className="relative rounded-2xl border border-purple-500/20 overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
              <img
                src="https://images.unsplash.com/photo-1760114333255-60fec662a268?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxtaW5lY3JhZnQlMjBzZXJ2ZXIlMjB0ZWNoJTIwZ2FtaW5nJTIwcHVycGxlJTIwZGFyayUyMGFlc3RoZXRpY3xlbnwwfHx8fDE3OTI3NDczNzJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Minecraft development environment"
                className="w-full h-72 sm:h-96 object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#090a0f]/80 via-transparent to-[#090a0f]/20" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
